import React from "react";
import {
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";

import { LottieIcon, PageContainer } from "src/shared/ui";
import { LottieIconNames } from "src/shared/config";

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageContainer
      hidden
      mainComponent={
        <>
          <Stack
            width={1}
            height={1}
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
            spacing={2}
            zIndex={99}
          >
            <Typography
              variant="h1"
              fontWeight="bold"
              color={blueGrey[700]}
              textTransform="uppercase"
              textAlign="center"
            >
              Privacy Policy
            </Typography>

            <Button variant="contained" size="large">
              Contact us
            </Button>
          </Stack>

          <LottieIcon
            icon={LottieIconNames.PRIVACY_POLICY}
            autoPlay
            loop
            style={{
              position: "absolute",
              height: "calc(100% - 180px)",
              transform: "translateX(150px)",
              top: 0,
              right: 0,
            }}
          />
        </>
      }
    >
      <Stack width={1} maxWidth="lg" direction="column" gap={2}>
        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Introduction
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          Welcome to MathrixAI. Your privacy is important to us. This Privacy
          Policy explains how we collect, use, store, and protect your personal
          information when you interact with our website, products, and
          services. We are committed to ensuring your data is handled securely
          and transparently.
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Data Controller
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          MathrixAI is owned and operated by Sven Badalyan. If you have any
          questions about this policy or your personal data, you can contact us
          at:
        </Typography>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack flex={1} direction="row" alignItems="center" gap={2}>
            <Chip
              label="Berlin Office"
              color="warning"
              sx={{
                width: 190,
                minWidth: 190,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={300}
              color="primary.dark"
            >
              Landsberger Allee 273, 13055 Berlin, Germany
            </Typography>
          </Stack>
          <Stack flex={1} direction="row" alignItems="center" gap={2}>
            <Chip
              label="Yerevan Office"
              color="warning"
              sx={{
                width: 190,
                minWidth: 190,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={300}
              color="primary.dark"
            >
              Hakob Hakobyan 3, 033 Yerevan, Armenia
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack flex={1} direction="row" alignItems="center" gap={2}>
            <Chip
              label="Phone"
              color="warning"
              sx={{
                width: 190,
                minWidth: 190,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={300}
              color="primary.dark"
            >
              +49 162 7344423
            </Typography>
          </Stack>
          <Stack flex={1} direction="row" alignItems="center" gap={2}>
            <Chip
              label="Email"
              color="warning"
              sx={{
                width: 190,
                minWidth: 190,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={300}
              color="primary.dark"
            >
              sven@badalyan.it
            </Typography>
          </Stack>
        </Stack>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Information We Collect
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          We collect various types of information, including but not limited to:
        </Typography>

        <List sx={{ listStyleType: "disc", marginLeft: 4 }}>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Personal Information</b> – Name, email address, phone number,
              and company details when you contact us, subscribe to our
              services, or interact with our website.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Browsing Data</b> – IP address, browser type, operating system,
              and device information collected automatically for website
              functionality and analytics.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Usage Information</b> – Data related to how you use our website
              and services, such as pages visited, time spent on the site, and
              interactions.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Other Voluntarily Provided Information</b> – Any additional
              details you choose to share with us.
            </Typography>
          </ListItem>
        </List>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          How We Use Your Information
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          We use your personal data for the following purposes:
        </Typography>

        <List sx={{ listStyleType: "disc", marginLeft: 4 }}>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              To provide, maintain, and improve our services.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              To communicate with you regarding inquiries, support, or updates.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              To conduct research, analytics, and performance optimization.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              To ensure compliance with legal and security obligations.
            </Typography>
          </ListItem>
        </List>

        <Typography variant="body1" fontWeight={300}>
          We do not use your data for automated decision-making or profiling
          without your explicit consent.
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Sharing of Information
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          We do not sell, rent, or trade your personal data. However, we may
          share your data in the following cases:
        </Typography>

        <List sx={{ listStyleType: "disc", marginLeft: 4 }}>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>With Service Providers</b> – Trusted third parties who assist
              in hosting, security, analytics, and technical support. These
              providers comply with strict data protection standards.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>When Required by Law</b> – If legally obligated to disclose
              data to authorities for compliance, fraud prevention, or legal
              proceedings.
            </Typography>
          </ListItem>
        </List>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Security of Your Personal Information
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          We implement strict security measures to protect your data from
          unauthorized access, misuse, or loss. These include:
        </Typography>

        <List sx={{ listStyleType: "disc", marginLeft: 4 }}>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Data Encryption</b> – Securing sensitive information using
              encryption protocols.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Access Control</b> – Limiting data access to authorized
              personnel only.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Regular Security Audits</b> – Monitoring and updating our
              security infrastructure.
            </Typography>
          </ListItem>
        </List>

        <Typography variant="body1" fontWeight={300}>
          While we take strong precautions, no system is completely secure. If a
          data breach occurs, we will take immediate action and notify affected
          users where required by law.
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Your Rights
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          Under GDPR and other data protection laws, you have the right to:
        </Typography>

        <List sx={{ listStyleType: "disc", marginLeft: 4 }}>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Access Your Data</b> – Request a copy of your personal data.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Rectify Inaccuracies</b> – Correct any incomplete or inaccurate
              information.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Request Deletion (“Right to be Forgotten”)</b> – Ask us to
              delete your data where legally applicable.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Restrict Processing</b> – Limit how we use your data under
              certain circumstances.
            </Typography>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <Typography variant="body1" fontWeight={300} color="primary.dark">
              <b>Withdraw Consent</b> – Opt out of marketing emails or withdraw
              any permissions you previously granted.
            </Typography>
          </ListItem>
        </List>

        <Typography variant="body1" fontWeight={300}>
          To exercise your rights, contact us. We will respond to your request
          within the legal timeframe.
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Changes to This Privacy Policy
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          We may update this Privacy Policy periodically to reflect changes in
          regulations or company practices. The latest version will always be
          available on our website, with the <b>“Last Updated”</b> date revised
          accordingly. We encourage users to review this page regularly to stay
          informed about how we protect your privacy.
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Contacting Us
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          If you have any questions about this Privacy Policy, the practices of
          this site, or your dealings with this site, please contact us at the
          details provided above.{" "}
        </Typography>
      </Stack>
    </PageContainer>
  );
};
